// QuickOpenModal — "Search Everywhere" (Ctrl+P): type a few letters and jump to
// any table / view / routine across ANY database on the active connection. Reads
// the per-connection schema cache (schema.js) — no extra round-trip — flattens it
// into one ranked list, and opens the chosen object via the host's callback.
//
// Props:
//   open            bool
//   connectionId    number  — the active connection (its schema cache is read)
//   currentDatabase string  — ranked higher + shown as the default scope
//   onClose()
//   onOpen(entry)   entry = { database, name, kind: 'table'|'view'|'function'|'procedure' }
import { createSignal, createEffect, createMemo, For, Show } from 'solid-js';
import Modal from './Modal.jsx';
import { completionState, loadCompletionSchema } from '../lib/schema.js';
import { rankObjects } from '../lib/quickopen.js';

const KIND_ICON = { table: '▦', view: '◫', function: 'ƒ', procedure: '⚙' };

export default function QuickOpenModal(props) {
  const [query, setQuery] = createSignal('');
  const [sel, setSel] = createSignal(0);
  let inputRef, listRef;

  // Load the schema the first time the palette opens for a connection.
  createEffect(() => {
    if (props.open && props.connectionId) loadCompletionSchema(props.connectionId);
  });
  // Reset the query each fresh open + focus the input.
  createEffect(() => {
    if (props.open) {
      setQuery('');
      setSel(0);
      queueMicrotask(() => inputRef?.focus());
    }
  });

  const state = () => (props.connectionId ? completionState(props.connectionId)() : { databases: [], building: false, loaded: false });

  // Flatten the cache once per (connection, schema) into a search index.
  const index = createMemo(() => {
    const out = [];
    for (const db of state().databases || []) {
      for (const t of db.tables || [])   out.push({ database: db.database, name: t.name, kind: 'table' });
      for (const v of db.views || [])    out.push({ database: db.database, name: v.name, kind: 'view' });
      for (const r of db.routines || []) out.push({ database: db.database, name: r.name, kind: (r.kind || '').toLowerCase() === 'function' ? 'function' : 'procedure' });
    }
    return out;
  });

  const results = createMemo(() => rankObjects(index(), query(), props.currentDatabase, 60));

  // Keep the selection in range whenever the result set changes.
  createEffect(() => { results(); setSel((s) => Math.min(s, Math.max(0, results().length - 1))); });

  const choose = (entry) => { if (entry) { props.onOpen?.(entry); props.onClose?.(); } };

  const onKey = (e) => {
    const n = results().length;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => (n ? (s + 1) % n : 0)); scrollSel(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => (n ? (s - 1 + n) % n : 0)); scrollSel(); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(results()[sel()]); }
    else if (e.key === 'Escape') { e.preventDefault(); props.onClose?.(); }
  };
  const scrollSel = () => queueMicrotask(() => {
    listRef?.querySelector('.quickopen-row.active')?.scrollIntoView({ block: 'nearest' });
  });

  return (
    <Show when={props.open}>
      <Modal open={true} title="Search Everywhere" onClose={props.onClose} cardClass="quickopen-modal">
        <div class="quickopen">
          <input
            ref={inputRef}
            class="input quickopen-input"
            placeholder="Jump to a table, view, or routine on this connection…"
            value={query()}
            onInput={(e) => { setQuery(e.currentTarget.value); setSel(0); }}
            onKeyDown={onKey}
          />
          <Show when={state().building}>
            <div class="muted" style="font-size:11px;padding:4px 2px;">Building schema cache… results will fill in.</div>
          </Show>
          <div class="quickopen-list" ref={listRef}>
            <Show when={results().length > 0}
                  fallback={<div class="muted" style="padding:14px;">{index().length === 0 ? 'No schema loaded for this connection yet.' : 'No matches.'}</div>}>
              <For each={results()}>
                {(r, i) => (
                  <div class="quickopen-row" classList={{ active: i() === sel() }}
                       onMouseEnter={() => setSel(i())}
                       onClick={() => choose(r)}>
                    <span class={`quickopen-kind quickopen-kind-${r.kind}`} title={r.kind}>{KIND_ICON[r.kind] || '•'}</span>
                    <span class="quickopen-name mono">{r.name}</span>
                    <span class="spacer" />
                    <span class="quickopen-db muted mono">{r.database}</span>
                  </div>
                )}
              </For>
            </Show>
          </div>
          <div class="quickopen-foot muted">
            <span><b>↑↓</b> navigate · <b>Enter</b> open · <b>Esc</b> close</span>
            <span class="spacer" />
            <span>{results().length} of {index().length}</span>
          </div>
        </div>
      </Modal>
    </Show>
  );
}
